import React from 'react';
import {Box, Chip, Stack, Typography} from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import {statusLabel} from '../../collection/collectionTaskPayload';

const KIND_BADGE_SX = {
    height: 20,
    fontSize: '0.65rem',
    width: 36,
    '& .MuiChip-label': {px: 0.5, width: '100%', textAlign: 'center'},
};

const ID_SX = {
    minWidth: 120,
    width: 120,
    flexShrink: 0,
    fontFamily: 'Roboto Mono, monospace',
    fontWeight: 700,
    color: 'primary.main',
};

function kindColor(kind) {
    if (kind === 'iir') return 'info';
    if (kind === 'cm') return 'secondary';
    return 'default';
}

function kindLabel(kind) {
    if (kind === 'iir') return 'IIR';
    if (kind === 'cm') return 'CM';
    return kind ? String(kind).toUpperCase() : 'Material';
}

function statusChipColor(status) {
    if (status === 'submitted' || status === 'approved') return 'success';
    return 'default';
}

function SourceKindIcon({sourceKind}) {
    if (sourceKind === 'url') return <LinkOutlinedIcon sx={{fontSize: 16}}/>;
    if (sourceKind === 'raw') return <NotesOutlinedIcon sx={{fontSize: 16}}/>;
    return <InsertDriveFileOutlinedIcon sx={{fontSize: 16}}/>;
}

/**
 * Row for a CM or IIR nested under a collection task on the EEI page.
 * Styled after the coverage CollectionMaterialRow but owned by P5 — clicking
 * anywhere opens the C2 collection-material viewer.
 */
export default function EeiCollectionMaterialRow({item, onOpen}) {
    if (!item) return null;
    const kind = item.materialKind || 'cm';
    const isCm = kind === 'cm';
    const displayId = item.cmNumber || item.iirNumber || item.id;
    const title = item.title || (kind === 'iir' ? 'Untitled IIR' : 'Untitled material');

    return (
        <Stack
            direction={{xs: 'column', sm: 'row'}}
            spacing={0.75}
            alignItems={{xs: 'flex-start', sm: 'center'}}
            sx={{
                py: 0.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                cursor: onOpen ? 'pointer' : 'default',
                '&:hover': onOpen ? {bgcolor: 'action.hover'} : undefined,
            }}
            onClick={onOpen ? () => onOpen(item) : undefined}
        >
            <Chip
                size="small"
                label={kindLabel(kind)}
                color={kindColor(kind)}
                sx={KIND_BADGE_SX}
            />
            <Typography variant="caption" sx={ID_SX} noWrap title={displayId}>
                {displayId}
            </Typography>
            <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{flex: 1, minWidth: 0}}
            >
                {isCm && (
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-flex',
                            color: 'text.secondary',
                            flexShrink: 0,
                            lineHeight: 0,
                        }}
                        title={item.sourceKind || 'file'}
                    >
                        <SourceKindIcon sourceKind={item.sourceKind}/>
                    </Box>
                )}
                <Typography
                    variant="body2"
                    noWrap
                    title={title}
                    sx={{minWidth: 0, textAlign: 'left'}}
                >
                    {title}
                </Typography>
                {isCm && item.collectionMethod && (
                    <Chip
                        size="small"
                        label={item.collectionMethod}
                        variant="outlined"
                        sx={{height: 20, fontSize: '0.65rem', flexShrink: 0}}
                    />
                )}
            </Stack>
            {item.status && (
                <Chip
                    size="small"
                    label={statusLabel(item.status)}
                    color={statusChipColor(item.status)}
                    variant={statusChipColor(item.status) === 'success' ? 'filled' : 'outlined'}
                    sx={{height: 20, fontSize: '0.65rem', flexShrink: 0}}
                />
            )}
        </Stack>
    );
}
